import { KpiInfo } from '@blisspointmedia/bpm-types/dist/Kpis';
import { GetKpiPickerMapResponse } from '@blisspointmedia/bpm-types/dist/Performance';
import {
  KPI_LABEL,
  KpiDimensionOption,
  NAME_DIMENSION,
  NON_DIMENSION_KEYS
} from './kpi-picker-constants';

const getKpiDimensions = (kpiInfo: KpiInfo, kpiFilterOrder: string[] | undefined): string[] => {
  const ownKeys = Object.keys(kpiInfo).filter(k => !NON_DIMENSION_KEYS.has(k));
  return Array.from(new Set([NAME_DIMENSION, ...(kpiFilterOrder ?? []), ...ownKeys]));
};

export const buildDropdownOptions = (
  kpi: string,
  kpiMap: GetKpiPickerMapResponse,
  kpiFilterOrder: string[] | undefined
): KpiDimensionOption[] | undefined => {
  const kpiInfo = kpiMap[kpi];
  if (!kpiInfo) {
    return undefined;
  }

  const dimensions = getKpiDimensions(kpiInfo, kpiFilterOrder);
  const dropdownOptions: KpiDimensionOption[] = [];
  let matches: KpiInfo[] = Object.values(kpiMap);

  for (const dimension of dimensions) {
    const ourDimensionValue = kpiInfo[dimension];
    if (!ourDimensionValue) {
      continue;
    }

    const dimensionOptions: string[] = [ourDimensionValue];
    const newMatches: KpiInfo[] = [];
    for (const candidate of matches) {
      const option = candidate[dimension];
      if (option) {
        dimensionOptions.push(option);
        if (option === ourDimensionValue) {
          newMatches.push(candidate);
        }
      }
    }
    matches = newMatches;
    dropdownOptions.push({
      dimension: dimension === NAME_DIMENSION ? KPI_LABEL : dimension,
      value: ourDimensionValue,
      options: Array.from(new Set(dimensionOptions))
    });
  }

  return dropdownOptions;
};

export const findMatchingKpi = (
  pickedDimension: string,
  pickedValue: string,
  currentDropdownOptions: KpiDimensionOption[],
  kpiMap: GetKpiPickerMapResponse,
  fallbackKpi: string
): string => {
  const ourOptions = [
    { dimension: pickedDimension, value: pickedValue },
    ...currentDropdownOptions.map(({ dimension, value }) => ({ dimension, value }))
  ];

  let candidates: KpiInfo[] = Object.values(kpiMap);
  for (const { dimension: rawDimension, value } of ourOptions) {
    const dimension = rawDimension === KPI_LABEL ? NAME_DIMENSION : rawDimension;
    const next = candidates.filter(candidate => candidate[dimension] === value);
    if (next.length) {
      candidates = next;
    }
  }

  return candidates[0]?.id || fallbackKpi;
};

export const buildCrossChannelKpiMap = (crossChannelKpis: string[]): GetKpiPickerMapResponse =>
  Object.fromEntries(
    crossChannelKpis.map(kpi => [kpi, { id: kpi, name: kpi, Type: '', Version: '' }])
  );

export const filterAndSortKpis = (
  kpis: KpiInfo[],
  filteredKpis: 'fetching' | Record<string, boolean> | undefined,
  applyFiltered: boolean,
  filter?: (kpi: string) => boolean
): { kpiMap: GetKpiPickerMapResponse; firstKpi: string } => {
  const map: GetKpiPickerMapResponse = {};
  let firstKpi: KpiInfo | null = null;

  for (const kpi of kpis) {
    const { id, hidden } = kpi;
    if (applyFiltered && filteredKpis && filteredKpis !== 'fetching' && !filteredKpis[id]) {
      continue;
    }
    if (hidden) {
      continue;
    }
    if (filter && !filter(id)) {
      continue;
    }
    map[id] = kpi;
    if (!firstKpi) {
      firstKpi = kpi;
    }
  }

  return { kpiMap: map, firstKpi: firstKpi?.id ?? '' };
};
